import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UserAccountRole, UserLocationRole } from '@shared/user/enums';
import { AppIcon } from '@tablesready/frontend-icons';
import { NavComponent } from './frontend-settings-nav.interfaces';
import { AuthUser } from '@shared/signup/models';
import { PermissionService } from '@tablesready/frontend-permissions';
import { combineLatest, concatMap, filter, of, Subscription, tap } from 'rxjs';
import { isOnFreePlan } from '@shared/user/functions';
import { AuthRestaurantService, LocationsService } from '@tablesready/frontend-store';
import { Router } from '@angular/router';

const managerReadPermission = 'manager:read';
const userAndInvites = 'Users & invites';
const organizationRoute = '/account/organization';

@Component({
  selector: 'frontend-settings-nav',
  templateUrl: './frontend-settings-nav.component.html',
  styleUrls: ['./frontend-settings-nav.component.scss'],
})
export class FrontendSettingsNavComponent implements OnInit, OnDestroy {
  @Output() itemClicked = new EventEmitter();
  user?: AuthUser;
  reservationTitle = '';
  reservationTitlePlural = '';
  isUserOnFreePlan = false;
  countOfTotalLocations?: number;
  isLocationManager = true;
  settingsNavItems = [];

  private readonly subscription = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly permissionService: PermissionService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly locationsService: LocationsService,
  ) {}

  linkClick(): void {
    this.itemClicked.emit();
  }

  ngOnInit(): void {
    const restSub = this.permissionService.userAccountHasScopesAsync(
      ['manager:read'],
      [UserAccountRole.user, UserAccountRole.admin],
    )
      .pipe(
        filter(data => !data),
        concatMap(() => this.authRestaurantService.select()),
      )
      .subscribe((user) => {
        this.user = user;
        this.update(user);
        this.buildNavItems();
      });
    this.subscription.add(restSub);

    const restaurantSub = combineLatest([
      this.locationsService.selectAll(),
      this.authRestaurantService.select(),
    ])
      .pipe(
        tap(([, user]) => {
          this.user = user;
          this.update(user);
        }),
        concatMap(([locations, user]) => {
          if (user.account && this.countOfTotalLocations === undefined) {
            return this.locationsService.load();
          }

          return of(locations);
        }),
      )
      .subscribe((locations) => {
        this.countOfTotalLocations = locations.length;
        this.buildNavItems();
      });
    this.subscription.add(restaurantSub);
  }

  update(user: AuthUser): void {
    this.reservationTitle = user.location?.displayOptions?.labels?.reservation || 'reservation';
    this.reservationTitlePlural = user.location?.displayOptions?.labels?.reservation_plural || 'reservations';
    this.isUserOnFreePlan = user.location ? isOnFreePlan(user) : false;
    this.isLocationManager = user.accountUser && user.accountUser.role === UserAccountRole.user &&
      user.locationUser && user.locationUser.role === UserLocationRole.manager;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.locationsService.reset();
  }

  buildNavItems(): void {
    const navComponents: NavComponent[] = [
      {
        href: '/account/profile',
        name: 'Preferences',
        class: 'dropdown',
        scopes: [],
        availableFor: [UserAccountRole.user, UserAccountRole.admin],
        subNav: [
          {
            href: '/preferences/notifications',
            name: 'Notifications',
            icon: AppIcon.notifications,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/waitlist',
            name: 'Waitlist Options',
            icon: AppIcon.waitlistOptions,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/reservation',
            name: `${this.reservationTitle} Options`,
            icon: AppIcon.reservationOptions,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/schedules',
            name: 'Schedules',
            icon: AppIcon.schedules,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/services',
            name: 'Services',
            icon: AppIcon.services,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/resources',
            name: 'Resources & Table management',
            icon: AppIcon.resources,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/public-pages',
            name: 'Public Pages',
            icon: AppIcon.publicPages,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/party-fields',
            name: 'Custom properties',
            icon: AppIcon.partyFields,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/alerts-settings',
            name: 'Alerts',
            icon: AppIcon.alerts,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/automations',
            name: 'Automations',
            icon: AppIcon.automations,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/integrations',
            name: 'Integrations',
            icon: AppIcon.integrations,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/account/profile',
            name: 'Profile',
            icon: AppIcon.accountInfo,
            scopes: [],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
        ],
      }];

    if (!this.isLocationManager && this.countOfTotalLocations === 1) {
      navComponents.push({
        href: organizationRoute,
        name: 'Account',
        class: 'dropdown',
        scopes: [managerReadPermission],
        availableFor: [UserAccountRole.admin],
        subNav: [

          {
            href: '/location/billing',
            name: 'Subscription Plan',
            icon: AppIcon.plans,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: organizationRoute,
            name: 'Organization',
            icon: AppIcon.organization,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.admin],
          },
          {
            href: '/account/locations',
            name: 'Locations',
            icon: AppIcon.locations,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.admin],
          },
          {
            href: '/account/users',
            name: userAndInvites,
            icon: AppIcon.users,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.admin],
          },
          {
            href: '/account/billing',
            name: 'Billing',
            icon: AppIcon.billing,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.admin],
          },
        ],
      });
    } else {
      navComponents.push(...[
        {
          href: '/location/info',
          name: 'Location management',
          class: 'dropdown',
          scopes: [managerReadPermission],
          availableFor: [UserAccountRole.user, UserAccountRole.admin],
          subNav: [
            {
              href: '/location/billing',
              name: 'Subscription Plan',
              icon: AppIcon.plans,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.user, UserAccountRole.admin],
            },
            {
              href: '/location/info',
              name: 'Location info',
              icon: AppIcon.location,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.user, UserAccountRole.admin],
            },
            {
              href: '/location/users',
              name: userAndInvites,
              icon: AppIcon.users,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.user, UserAccountRole.admin],
            },
          ],
        },
        {
          href: organizationRoute,
          name: 'Account',
          class: 'dropdown',
          scopes: [managerReadPermission],
          availableFor: [UserAccountRole.admin],
          subNav: [
            {
              href: organizationRoute,
              name: 'Organization',
              icon: AppIcon.organization,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.admin],
            },
            {
              href: '/account/locations',
              name: 'Locations',
              icon: AppIcon.locations,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.admin],
            },
            {
              href: '/account/users',
              name: userAndInvites,
              icon: AppIcon.users,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.admin],
            },
            {
              href: '/account/billing',
              name: 'Billing',
              icon: AppIcon.billing,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.admin],
            },
          ],
        },
      ]);
    }

    this.settingsNavItems = navComponents.map((parentNav) => {
      if (!this.user) {
        return {
          href: '',
          name: '',
          scopes: [],
          subNav: undefined,
        };
      }

      const { scopes, availableFor, subNav } = parentNav;
      const isParentNavHasScope = this.user.accountUser
        ? this.permissionService.userAccountHasScopes(this.user, scopes, availableFor)
        : this.permissionService.hasScopes(this.user, scopes);

      if (isParentNavHasScope) {
        const subNavs = subNav.filter((item) => {
          if (!this.user) {
            return false;
          }

          return this.permissionService
            .userAccountHasScopes(this.user, item.scopes, item.availableFor);
        });

        return {
          href: parentNav.href,
          name: parentNav.name,
          scopes: parentNav.scopes,
          subNav: subNavs,
        };
      }

      return {
        href: '',
        name: '',
        scopes: [],
        subNav: undefined,
      };
    }).filter(elem => elem !== null);
  }
}
